<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef } from 'vue'

import { Icon } from '@iconify/vue'
import { storeToRefs } from 'pinia'

import UserAvatar from '@/components/UI/UserAvatar.vue'

import { loadMemberImages, transformIdentityToMember } from '@/utils/common/memberHelper'
import { isEmail } from '@/utils/validation/commonValidator'

import * as aggr from '@/api-generator/aggr'
import { identityPickerAPI } from '@/APIs'
import { MEMBER_COLLECT_TYPE, MEMBER_COLLECT_ERRORS, type MemberCollectType } from '@/constant/admin/memberCollect'
import appStore from '@/stores'

import type { Member } from '@/types/userManage'

const { selectedCompanyUuId } = storeToRefs(appStore.useShortUrlStore)

interface Props {
  label: string
  required?: boolean
  type: MemberCollectType
  identityType: aggr.IdentityPickerType
  tooltipContent?: string
  collectInFrontend?: boolean
  autoFocus?: boolean
  /** 已存在的成員 email 列表，用於在下拉選單中標記為已邀請 */
  existingMemberEmails?: Set<string>
}

const {
  label = '',
  required = false,
  tooltipContent,
  type = MEMBER_COLLECT_TYPE.INPUT,
  identityType = aggr.IdentityPickerType.None,
  collectInFrontend = false,
  autoFocus = false,
  existingMemberEmails = new Set<string>()
} = defineProps<Props>()

const list = defineModel<Member[]>('list', {
  default: () => []
})

const inputValue = ref('')

/** 判斷成員是否已存在於已邀請列表中 */
const isMemberExisting = (member: Member): boolean => {
  if (!existingMemberEmails || existingMemberEmails.size === 0) {
    return false
  }
  return !!(member.email && existingMemberEmails.has(member.email))
}

const fetchInputMembers = async (queryString: string): Promise<Member[]> => {
  const { data } =
    collectInFrontend && selectedCompanyUuId.value
      ? await identityPickerAPI.getIdentities(queryString, identityType, selectedCompanyUuId.value)
      : await identityPickerAPI.getIdentities(queryString, identityType)

  const members = data.map(transformIdentityToMember)

  await loadMemberImages(members)

  return members
}

const searchInputMember = (queryString: string, cb: (data: Member[]) => void) => {
  fetchInputMembers(queryString)
    .then(cb)
    .catch((error) => {
      console.error('Failed to fetch members:', error)
      cb([])
    })
}

const selectData = ref<Member>()
const members = ref<Member[]>([])
const loading = ref(false)
const searchString = ref('')

const searchSelectMember = async (queryString: string) => {
  try {
    searchString.value = queryString
    loading.value = true
    const { data } = await identityPickerAPI.getIdentities(queryString, identityType)
    members.value = data.map(transformIdentityToMember)

    await loadMemberImages(members.value)
  } catch (error) {
    console.error('Failed to search members:', error)
    members.value = []
  } finally {
    loading.value = false
  }
}

/** 判斷成員是否已在當前列表中 */
const isMemberInList = (email: string): boolean => {
  return list.value.some((m) => m.email === email)
}

const handleSelect = (item: Member) => {
  if (isMemberExisting(item) || (item.email && isMemberInList(item.email))) {
    inputValue.value = ''
    return
  }
  list.value.push(item)
  resetInputState()
}

const errorMsg = ref<string>('')

/** 透過輸入框新增成員（手動輸入 email） */
const addMemberByInput = () => {
  const email = inputValue.value
  if (!email) {
    return
  }

  // 檢查是否已存在於組織中
  if (existingMemberEmails.has(email)) {
    ElMessage.error('該成員已存在於組織中')
    resetInputState()
    return
  }

  // 檢查是否已在當前列表中
  if (isMemberInList(email)) {
    ElMessage.error('該成員已在列表中')
    resetInputState()
    return
  }

  if (!isEmail(email)) {
    errorMsg.value = MEMBER_COLLECT_ERRORS.INVALID_EMAIL
    return
  }

  list.value.push({ email, value: email })
  resetInputState()
}

/** 透過下拉選單新增成員 */
const addMemberBySelect = () => {
  if (!selectData.value) {
    return
  }

  list.value.push(selectData.value)
  selectData.value = undefined
  searchString.value = ''
  tabBlockInputRef.value?.blur()
}

/** 重置輸入框狀態 */
const resetInputState = () => {
  inputValue.value = ''
  errorMsg.value = ''
}

const addMember = () => {
  if (type === MEMBER_COLLECT_TYPE.INPUT) {
    addMemberByInput()
  } else if (type === MEMBER_COLLECT_TYPE.SELECT) {
    addMemberBySelect()
  }
}

const removeMember = (item: Member) => {
  const index = list.value.indexOf(item)
  if (index !== -1) {
    list.value.splice(index, 1)
  }
}

const deleteLastTag = (e: KeyboardEvent) => {
  if (
    (e.key === 'Backspace' || e.key === 'Delete') &&
    list.value.length > 0 &&
    inputValue.value === '' &&
    searchString.value === ''
  ) {
    list.value.pop()
  }
}

const tabBlockInputRef = useTemplateRef<{ focus: () => void; blur: () => void }>('tabBlockInputRef')
const clickTagBlock = () => {
  tabBlockInputRef.value?.focus()
}

onMounted(async () => {
  await nextTick()
  if (tabBlockInputRef.value && autoFocus) {
    tabBlockInputRef.value.focus()
  }
})

defineExpose({
  clearInput: resetInputState
})
</script>

<template>
  <div class="member-collect">
    <el-form ref="member-form" :label-position="'top'">
      <div class="add-input-row">
        <el-form-item
          :label="label"
          :required="required"
          class="input"
          :style="collectInFrontend ? 'font-weight: 700;' : ''"
        >
          <template v-if="tooltipContent" #label>
            <el-tooltip effect="dark" :content="tooltipContent" placement="right">
              <span class="label">{{ label }}<img class="icon" src="@/assets/image/info-icon.svg" alt="" /></span>
            </el-tooltip>
          </template>
          <div class="form-tag-block" :class="{ 'px-3 py-2': list.length > 0 }" @click="clickTagBlock">
            <el-tag
              v-for="(member, index) in list"
              :key="member.email ?? index"
              type="info"
              effect="plain"
              size="large"
              round
              closable
              class="tag"
              @close="removeMember(member)"
            >
              <UserAvatar v-if="member.userUuid" class="avatar" :size="20" :src="member.blobImg" />
              <span class="name">{{ member.value }}</span>
            </el-tag>
            <el-autocomplete
              v-if="type === MEMBER_COLLECT_TYPE.INPUT"
              ref="tabBlockInputRef"
              v-model="inputValue"
              class="block-input"
              popper-class="member-collect-popper"
              :fetch-suggestions="searchInputMember"
              :trigger-on-focus="false"
              style="font-weight: 400"
              @select="handleSelect"
              @keypress.enter="addMember"
              @keydown.delete="deleteLastTag"
              @keypress.enter.prevent
            >
              <template #default="{ item: member }">
                <div
                  class="suggest-item"
                  :class="
                    isMemberExisting(member)
                      ? 'cursor-not-allowed text-app-black-500'
                      : 'cursor-pointer text-app-black-900'
                  "
                >
                  <UserAvatar class="avatar" :size="18" :src="member.blobImg" />
                  <span class="name">{{ member.userName }}</span>
                  <span class="email">{{ member.email }}</span>
                  <Icon
                    v-if="isMemberExisting(member)"
                    class="ml-auto shrink-0 text-app-black-800"
                    icon="ri:check-line"
                    width="16px"
                    height="16px"
                  />
                </div>
              </template>
            </el-autocomplete>
            <el-select
              v-if="type === MEMBER_COLLECT_TYPE.SELECT"
              ref="tabBlockInputRef"
              v-model="selectData"
              class="block-input"
              placeholder="請選擇成員"
              filterable
              remote
              :remote-method="searchSelectMember"
              :loading="loading"
              @change="addMember"
              @keydown.delete="deleteLastTag"
            >
              <el-option
                v-for="(item, index) in members"
                :key="`${item.userUuid}-${index}`"
                :value="item"
                :label="item.value"
              >
                <template #default>
                  <div class="suggest-item">
                    <UserAvatar class="avatar" :size="18" :src="item.blobImg" />
                    <span class="name">{{ item.userName }}</span>
                    <span class="email">{{ item.email }}</span>
                  </div>
                </template>
              </el-option>
            </el-select>
          </div>
        </el-form-item>
      </div>
    </el-form>
    <span v-if="errorMsg" class="error-msg">{{ errorMsg }}</span>
  </div>
</template>

<style lang="scss" scoped>
.member-collect {
  .form-tag-block {
    width: 100%;
    border: 1px solid var(--gray-d9);
    border-radius: 8px;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 6px;
    transition: border-color 0.2s ease;
    min-height: 32px;
    padding-top: 6px;
    padding-bottom: 6px;

    &:focus-within {
      border-color: rgb(var(--color-brand-blue));
    }

    .tag {
      height: 32px;
      :deep(.el-tag__content) {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .avatar {
        margin-right: 6px;
        margin-left: -8px;
      }

      .name {
        font-size: 14px;
        font-weight: 400;
        color: black;
      }
    }

    .block-input {
      border: none;
      outline: none;
      height: 30px;
      box-shadow: none !important;
      width: 150px;

      .el-input__wrapper {
        box-shadow: none !important;
      }

      .el-input__wrapper.is-focus {
        box-shadow: none !important;
      }
    }
  }

  .label {
    .icon {
      margin-left: 3px;
      margin-bottom: -2px;
    }
  }

  .error-msg {
    color: var(--danger);
    font-size: 12px;
    position: absolute;
    margin-top: -16px;
  }
}

:deep(.el-select__wrapper) {
  box-shadow: none !important;
}

:deep(.el-input__wrapper) {
  box-shadow: none !important;
  border-radius: 8px;
}

.suggest-item {
  display: flex;
  align-items: center;

  .avatar {
    flex-shrink: 0;
  }

  .name {
    margin-left: 6px;
    margin-right: 10px;
    font-weight: 400;
    font-size: 14px;
    flex-shrink: 0;
  }

  .email {
    color: var(--gray-7d);
    font-weight: 400;
    font-size: 12px;
    flex-shrink: 0;
  }
}

.member-collect-popper li:has(.cursor-not-allowed) {
  cursor: not-allowed;
}
</style>
